import './styles.css'
import React, { FC, useCallback, useEffect, useRef } from 'react'

import { useMutationObserver } from 'components/use-mutation-observer'

import { cn } from 'utils'

const EXTRA_SPACE = 1

export const ExpandableContent: FC<{ isOpen: boolean; className?: string }> = ({
  isOpen,
  className,
  children,
}) => {
  const parent = useRef(null)
  const prevHeight = useRef(0)
  useEffect(() => {
    const wrapper = parent?.current
    const firstChildren = wrapper?.children?.[0]
    const lastChildren = wrapper?.children?.[wrapper.children.length - 1]

    const resizeOb = new ResizeObserver((entries) => {
      // since we are observing only a single element, so we access the first element in entries array
      const rect = entries[0].contentRect

      if (!wrapper) {
        return
      }

      const firstChildTop = (firstChildren || wrapper)?.getBoundingClientRect().top
      const lastChildBottom = (lastChildren || wrapper)?.getBoundingClientRect().bottom
      if (prevHeight.current && prevHeight.current === rect.height) {
        const newHeight = lastChildBottom - firstChildTop + EXTRA_SPACE
        wrapper.style.maxHeight = newHeight + 'px'
        prevHeight.current = newHeight
      }
    })
    resizeOb.observe(wrapper)
    return () => resizeOb.unobserve(wrapper)
  }, [])

  useEffect(() => {
    const wrapper = parent?.current
    const newHeight = isOpen ? wrapper.scrollHeight + EXTRA_SPACE : 0
    wrapper.style.maxHeight = newHeight + 'px'
    prevHeight.current = newHeight
  }, [isOpen])

  const callback = useCallback(
    (mutationList, observer) => {
      const wrapper = parent?.current
      for (const mutation of mutationList) {
        if (mutation.type === 'childList') {
          if (isOpen) {
            wrapper.style.maxHeight = wrapper.scrollHeight + EXTRA_SPACE + 'px'
            prevHeight.current = wrapper.scrollHeight + EXTRA_SPACE
          }
        }
      }
    },
    [isOpen],
  )

  useMutationObserver(parent?.current, callback)

  return (
    <div className={cn('expandable-content', isOpen && 'open', className)} ref={parent}>
      {children}
    </div>
  )
}
